import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import NewPostModal from './NewPostModal';
import './UnifiedFeed.css';

const STATES = [
  '', 'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

const getTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const UnifiedFeed = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('clubs');
  const [stateFilter, setStateFilter] = useState('');
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showNewPost, setShowNewPost] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [videoModal, setVideoModal] = useState(null);

  const fetchPosts = useCallback(async (pageNum = 1, reset = false) => {
    try {
      const params = new URLSearchParams({
        type: activeTab,
        page: pageNum,
        sort: 'mostRecent',
      });
      if (stateFilter) params.set('state', stateFilter);

      const res = await axiosInstance.get(`/messageboard/mobile/feed/?${params.toString()}`);
      const data = res.data;

      if (reset) {
        setPosts(data.results || []);
        setPage(2);
      } else {
        setPosts(prev => [...prev, ...(data.results || [])]);
        setPage(prev => prev + 1);
      }
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error('Error fetching feed:', error);
    }
  }, [activeTab, stateFilter]);

  useEffect(() => {
    setLoading(true);
    setPosts([]);
    fetchPosts(1, true).finally(() => setLoading(false));
  }, [fetchPosts]);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setStateFilter('');
  };

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    fetchPosts(page, false).finally(() => setLoadingMore(false));
  };

  const handleVote = async (post, voteType) => {
    try {
      const res = await axiosInstance.post('/messageboard/mobile/vote/', {
        post_id: post.id,
        post_type: post.type,
        vote_type: voteType,
      });
      setPosts(prev => prev.map(p =>
        p.id === post.id && p.type === post.type
          ? { ...p, upvotes: res.data.upvotes, downvotes: res.data.downvotes }
          : p
      ));
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const handlePostClick = (post) => {
    if (post.type === 'club') {
      navigate(`/post/${post.id}`);
    } else {
      const state = post.forum_info?.state;
      if (state) {
        navigate(`/forums/${state}/${post.id}`);
      } else {
        navigate(`/forums/usa/${post.id}`);
      }
    }
  };

  const handlePostCreated = () => {
    fetchPosts(1, true);
  };

  // Render media
  const renderMedia = (post) => {
    if (post.video) {
      return (
        <div className="uf-media">
          <div className="uf-video" onClick={(e) => { e.stopPropagation(); setVideoModal(post.video.video_url); }}>
            <img src={post.video.thumbnail_url} alt="Video thumbnail" />
            <div className="uf-video-play" />
            {post.video.duration && (
              <span className="uf-video-duration">{Math.round(post.video.duration)}s</span>
            )}
          </div>
        </div>
      );
    }

    if (post.images && post.images.length > 0) {
      const images = post.images;
      if (images.length === 1) {
        return (
          <div className="uf-media uf-media-single" onClick={(e) => { e.stopPropagation(); setLightboxImage(images[0].image_url); }}>
            <img src={images[0].image_url} alt="Post" loading="lazy" />
          </div>
        );
      }
      const gridClass = images.length === 2 ? 'uf-media-grid-2' : images.length === 3 ? 'uf-media-grid-3' : 'uf-media-grid-4';
      const displayImages = images.slice(0, 4);
      const extraCount = images.length - 4;
      return (
        <div className={`uf-media uf-media-grid ${gridClass}`}>
          {displayImages.map((img, i) => (
            <div
              key={img.id || i}
              className={i === 3 && extraCount > 0 ? 'uf-media-extra' : ''}
              data-extra={extraCount > 0 ? `+${extraCount}` : ''}
              onClick={(e) => { e.stopPropagation(); setLightboxImage(img.image_url); }}
            >
              <img src={img.image_url} alt={`Post ${i + 1}`} loading="lazy" />
            </div>
          ))}
        </div>
      );
    }

    if (post.gif_url) {
      return (
        <div className="uf-media uf-gif" onClick={(e) => { e.stopPropagation(); setLightboxImage(post.gif_url); }}>
          <img src={post.gif_url} alt="GIF" loading="lazy" />
          <span className="uf-gif-badge">GIF</span>
        </div>
      );
    }

    return null;
  };

  // Render single post card
  const renderPost = (post) => {
    const isClub = post.type === 'club';
    const clubName = isClub ? (post.location?.club_name || post.location?.course_name) : null;
    const titleCase = (str) => str ? str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '';
    const location = isClub
      ? [post.location?.city ? titleCase(post.location.city) : null, post.location?.state ? post.location.state.toUpperCase() : null].filter(Boolean).join(', ')
      : (post.forum_info?.is_general ? 'General Forum' : `${post.forum_info?.state || ''} Forum`);

    return (
      <div key={`${post.type}-${post.id}`} className="uf-post" onClick={() => handlePostClick(post)}>
        {/* Header */}
        <div className="uf-post-header">
          <div className="uf-post-meta">
            <Link
              to={`/memberprofile/${post.username}`}
              className="uf-post-username"
              onClick={(e) => e.stopPropagation()}
            >
              @{post.username}
            </Link>
            {clubName && (
              <>
                <span className="uf-post-dot">·</span>
                <Link
                  to={post.location?.club_slug ? `/club/${post.location.club_slug}` : (post.location?.club_id ? `/club/${post.location.club_id}` : '#')}
                  className="uf-post-club-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {clubName}
                </Link>
              </>
            )}
            {!isClub && (
              <>
                <span className="uf-post-dot">·</span>
                <span className="uf-forum-badge">{location}</span>
              </>
            )}
            {isClub && location && (
              <>
                <span className="uf-post-dot">·</span>
                <span className="uf-post-location">{location}</span>
              </>
            )}
          </div>
          <span className="uf-post-time">{getTimeAgo(post.created_at)}</span>
        </div>

        {/* Content */}
        {post.title && <h3 className="uf-post-title">{post.title}</h3>}
        {post.content && <p className="uf-post-body">{post.content}</p>}

        {/* Link */}
        {post.link_url && (
          <a href={post.link_url} className="uf-post-link" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            {post.link_url.replace(/^https?:\/\//, '').split('/')[0]}
          </a>
        )}

        {/* Media */}
        {renderMedia(post)}

        {/* Actions */}
        <div className="uf-actions">
          <button className="uf-action" onClick={(e) => { e.stopPropagation(); handleVote(post, 'upvote'); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>
            {post.upvotes || 0}
          </button>
          <button className="uf-action" onClick={(e) => { e.stopPropagation(); handleVote(post, 'downvote'); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
            {post.downvotes || 0}
          </button>
          <button className="uf-action" onClick={(e) => { e.stopPropagation(); handlePostClick(post); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            {post.reply_count || 0}
          </button>
          <div className="uf-actions-spacer" />
        </div>
      </div>
    );
  };

  return (
    <div className="uf-page">
      {/* Header */}
      <div className="uf-header">
        <h1 className="uf-title">Feed</h1>
        <button className="uf-create-btn" onClick={() => setShowNewPost(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Post
        </button>
      </div>

      {/* New Post Modal */}
      <NewPostModal
        visible={showNewPost}
        onClose={() => setShowNewPost(false)}
        onPostCreated={handlePostCreated}
      />

      {/* Tabs + Filter */}
      <div className="uf-controls">
        <div className="uf-tabs">
          <button className={`uf-tab ${activeTab === 'clubs' ? 'active' : ''}`} onClick={() => handleTabChange('clubs')}>
            Clubs
          </button>
          <button className={`uf-tab ${activeTab === 'forums' ? 'active' : ''}`} onClick={() => handleTabChange('forums')}>
            Forums
          </button>
        </div>
        <div className="uf-filter">
          <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            <option value="">All States</option>
            {STATES.filter(s => s).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Posts */}
      {loading ? (
        <div className="uf-loading">Loading posts...</div>
      ) : posts.length === 0 ? (
        <div className="uf-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <h3>No posts yet</h3>
          <p>Be the first to share something in {activeTab === 'clubs' ? 'club discussions' : 'the forums'}</p>
        </div>
      ) : (
        <>
          {posts.map(renderPost)}
          {hasMore && (
            <div className="uf-load-more">
              <button onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div className="uf-lightbox" onClick={() => setLightboxImage(null)}>
          <img src={lightboxImage} alt="Full size" />
          <button className="uf-lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
        </div>
      )}

      {/* Video Modal */}
      {videoModal && (
        <div className="uf-video-modal" onClick={() => setVideoModal(null)}>
          <video src={videoModal} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '90vh' }} />
          <button className="uf-video-modal-close" onClick={() => setVideoModal(null)}>×</button>
        </div>
      )}

    </div>
  );
};

export default UnifiedFeed;
